import React, { useState, useEffect } from 'react';
import { useDynamicRoutes } from '../hooks/useDynamicRoutes';
import { useLocation } from 'react-router-dom';

/**
 * Componente de monitoreo para rutas dinámicas
 * Por defecto está deshabilitado, se habilita manualmente via consola
 */
const DynamicRoutesMonitor = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const { dynamicRoutes, isLoading, hasRoutes } = useDynamicRoutes();
    const location = useLocation();
    
    // Sistema de debug controlado por consola global
    useEffect(() => {
        const debugEnabled = localStorage.getItem('dynamicRoutesDebug') === 'true';
        
        // Exponer funciones globales para habilitar/deshabilitar
        window.enableRoutesDebug = () => {
            localStorage.setItem('dynamicRoutesDebug', 'true');
            setIsVisible(true);
            console.log('🚀 Dynamic Routes Debug ENABLED');
            console.log('📍 Monitor will appear in bottom-right corner');
        };
        
        window.disableRoutesDebug = () => {
            localStorage.removeItem('dynamicRoutesDebug');
            setIsVisible(false);
            console.log('❌ Dynamic Routes Debug DISABLED');
        };
        
        // Función para mostrar ayuda
        window.routesDebugHelp = () => {
            console.log('🔧 Dynamic Routes Debug Commands:');
            console.log('   enableRoutesDebug()  - Habilitar monitor');
            console.log('   disableRoutesDebug() - Deshabilitar monitor');
            console.log('   routesDebugHelp()    - Mostrar esta ayuda');
            console.log('💡 El monitor está deshabilitado por defecto para mejor rendimiento');
        };
        
        // Estado inicial basado solo en localStorage
        setIsVisible(debugEnabled);
        
        // Mostrar ayuda inicial solo una vez
        if (!debugEnabled && !sessionStorage.getItem('routesDebugHelpShown')) {
            console.log('💡 Para depurar rutas dinámicas, usa: enableRoutesDebug()');
            sessionStorage.setItem('routesDebugHelpShown', 'true');
        }
    }, []);
    
    if (!isVisible) return null;
    
    const currentPath = location.pathname;
    const isDynamicRoute = dynamicRoutes.some(route => route.path === currentPath);
    const isManualDebug = localStorage.getItem('dynamicRoutesDebug') === 'true';
    
    const monitorStyles = {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        backgroundColor: '#1e293b',
        color: 'white',
        padding: '12px',
        borderRadius: '8px',
        fontSize: '12px',
        zIndex: 9999,
        maxWidth: '300px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        border: '1px solid #334155'
    };
    
    const headerStyles = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        fontSize: '14px',
        fontWeight: 'bold'
    };
    
    const buttonStyles = {
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '16px',
        padding: '2px 6px',
        borderRadius: '4px',
        hover: {
            backgroundColor: '#475569'
        }
    };
    
    const statusStyles = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
    };
    
    const dotStyles = {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: isDynamicRoute ? '#10b981' : '#6b7280'
    };
    
    return (
        <div style={monitorStyles}>
            <div style={headerStyles}>
                <span>🚀 Rutas dinámicas</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isManualDebug && (
                        <span style={{ 
                            fontSize: '10px', 
                            backgroundColor: '#10b981', 
                            color: '#000', 
                            padding: '2px 6px', 
                            borderRadius: '4px' 
                        }}>
                            DEBUG
                        </span>
                    )}
                    <button 
                        style={buttonStyles}
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? '−' : '+'}
                    </button>
                </div>
            </div>
            
            <div style={statusStyles}>
                <div style={dotStyles}></div>
                <span>
                    {isLoading ? 'Loading...' : `${dynamicRoutes.length} rutas`}
                </span>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
                <strong>Actual:</strong> {currentPath}
                {isDynamicRoute && <span style={{ color: '#10b981' }}> (Dinámica)</span>}
            </div>
            
            {isExpanded && hasRoutes && (
                <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    borderTop: '1px solid #475569',
                    paddingTop: '8px',
                    marginTop: '8px'
                }}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                        Dynamic Routes:
                    </div>
                    {dynamicRoutes.map((route, index) => {
                        const isExplicit = route.explicitComponent;
                        return (
                            <div 
                                key={route.key}
                                style={{ 
                                    marginBottom: '4px',
                                    padding: '4px',
                                    backgroundColor: route.path === currentPath ? '#10b981' : 'transparent',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    borderLeft: isExplicit ? '3px solid #f59e0b' : '3px solid transparent',
                                    paddingLeft: '8px'
                                }}
                            >
                                <div>{route.path}</div>
                                {isExplicit && (
                                    <div style={{ 
                                        fontSize: '10px', 
                                        color: '#f59e0b', 
                                        marginTop: '2px' 
                                    }}>
                                        📌 {route.explicitComponent}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    
                    {/* Estadísticas */}
                    <div style={{ 
                        borderTop: '1px solid #475569',
                        paddingTop: '8px',
                        marginTop: '8px',
                        fontSize: '10px',
                        color: '#9ca3af'
                    }}>
                        📌 {dynamicRoutes.filter(r => r.explicitComponent).length} Explicita | 
                        📏 {dynamicRoutes.filter(r => !r.explicitComponent).length} Basada en reglas
                    </div>
                    
                    {/* Botón para deshabilitar debug */}
                    {isManualDebug && (
                        <div style={{ 
                            borderTop: '1px solid #475569',
                            paddingTop: '8px',
                            marginTop: '8px',
                            textAlign: 'center'
                        }}>
                            <button 
                                style={{
                                    ...buttonStyles,
                                    backgroundColor: '#dc2626',
                                    fontSize: '10px',
                                    padding: '4px 8px'
                                }}
                                onClick={() => window.disableRoutesDebug()}
                            >
                                🔒 Deshabilitar Debug
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DynamicRoutesMonitor;
